import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Box,
    Button,
    Column,
    Columns,
    IconAdd,
    IconEdit,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { ItemGroupResponse } from "../../services/itemGroupService";

interface ItemGroupListTableProps {
    itemGroupList?: Array<ItemGroupResponse> | null;
    onCreateItemGroup?: () => void;
    onEditItemGroup?: (group: ItemGroupResponse) => void;
}

const ItemGroupListTable: React.FC<ItemGroupListTableProps> = ({
    itemGroupList,
    onCreateItemGroup,
    onEditItemGroup,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");

    const filteredGroups = useMemo(() => {
        if (!itemGroupList) {
            return itemGroupList;
        }

        return itemGroupList.filter((group) => {
            const normalizedSearch = searchTerm.trim().toLowerCase();

            const matchesSearch =
                !normalizedSearch ||
                group.group_code?.toLowerCase().includes(normalizedSearch) ||
                group.group_name?.toLowerCase().includes(normalizedSearch);

            return matchesSearch;
        });
    }, [itemGroupList, searchTerm]);

    const showActions = typeof onEditItemGroup === "function";

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart">
                        {onCreateItemGroup ? (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreateItemGroup}
                                >
                                    {t("page.item_group_management.list.button.create_new", {
                                        defaultValue: "Create New Item Group",
                                    })}
                                </Button>
                            </Actions>
                        ) : null}
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd" alignItems="center">
                        <TextField
                            placeholder={t("page.item_group_management.list.search.placeholder", {
                                defaultValue: "Search Item Groups...",
                            })}
                            aria-label={t("page.item_group_management.list.search.aria", {
                                defaultValue: "Search Item Groups",
                            })}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            value={searchTerm}
                        />
                    </Box>
                </Column>
            </Columns>

            <Table
                label={t("page.item_group_management.list.table.label", {
                    defaultValue: "Item Groups List Table",
                })}
            >
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.code", { defaultValue: "Code" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.icon", { defaultValue: "Icon" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.name", { defaultValue: "Group Name" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.saccno", { defaultValue: "Sales A/C" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.caccno", { defaultValue: "COS A/C" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.iaccno", { defaultValue: "Inventory A/C" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.reorder", { defaultValue: "Reorder Point" })}</Text></TableHeaderCell>
                        <TableHeaderCell><Text>{t("page.item_group_management.list.table.headers.prchadays", { defaultValue: "Price History Days" })}</Text></TableHeaderCell>
                        {showActions ? <TableHeaderCell align="right"><Text>{t("page.item_group_management.list.table.headers.actions", { defaultValue: "Actions" })}</Text></TableHeaderCell> : null}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {filteredGroups && filteredGroups.length > 0 ? (
                        filteredGroups.map((group) => (
                            <TableRow key={group.group_id ?? group.group_code ?? "item-group-row"}>
                                <TableCell><Text>{group.group_code || t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{group.group_icon || "🏷️"}</Text></TableCell>
                                <TableCell><Text>{group.group_name || t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{group.group_saccno || t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{group.group_caccno || t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{group.group_iaccno || t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{group.group_reorder != null ? group.group_reorder : t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                <TableCell><Text>{group.group_prchadays != null ? group.group_prchadays : t("page.item_group_management.list.table.na", { defaultValue: "N/A" })}</Text></TableCell>
                                {showActions ? (
                                    <TableCell align="right">
                                        <Inline space="small">
                                            <TooltipRenderer tooltip={<Text>{t("page.item_group_management.list.tooltip.edit", { defaultValue: "Edit Item Group" })}</Text>}>
                                                {({ triggerProps }) => (
                                                    <Box
                                                        onClick={() => onEditItemGroup && onEditItemGroup(group)}
                                                        cursor="pointer"
                                                        {...triggerProps}
                                                    >
                                                        <IconEdit tone="info" />
                                                    </Box>
                                                )}
                                            </TooltipRenderer>
                                        </Inline>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colspan={showActions ? 9 : 8} align="center">
                                <Text>{t("page.item_group_management.list.empty", { defaultValue: "No item groups found." })}</Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default ItemGroupListTable;